<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';
import { format } from 'date-fns';
import { useOffersStore } from '@/stores/offers';
import { useErrorStore } from '@/stores/error';
import { useOptionsStore } from '@/stores/options';
import { useUserStore } from '@/stores/user';
import { isRole } from '@/utils/roleHelper';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
const offersStore = useOffersStore();
const errorStore = useErrorStore();
const errors = errorStore.all;
const optionsStore = useOptionsStore();
const userStore = useUserStore();
const { offerRequest } = storeToRefs(offersStore);
const { profile } = storeToRefs(userStore);
const {
    yesNoOptions,
    overallConditionOptions,
    propertyTypeOptions,
    styleAllOptions,
    bedroomsOptions,
    bathroomsOptions,
    kitchensOptions,
    basementDevelopmentOptions,
    heatingTypeOptions,
    garageParkingOptions,
    floorCoveringsOptions,
    timelineOptions,
    sellerMotivationOptions
} = storeToRefs(optionsStore);
const ageOptions = computed(() => optionsStore.offerOptionLists.age);
const provinceOptions = computed(() => optionsStore.provinceOptions);

const toast = useToast();

const backToOffers = () => {
    if (isLead.value) {
        window.location.href = '/leads';
    } else {
        window.location.href = '/offers';
    }
};

const tabs = [
    { name: 'Basic Information', id: 'basic-information' },
    { name: 'Property Details', id: 'property-details' },
    { name: 'Rooms & Features', id: 'rooms-features' },
    { name: 'Condition & Age', id: 'condition-age' },
    { name: 'Seller\'s Information', id: 'sale-information-valuation' },
    { name: 'Agent Contact Information', id: 'agents-contact-information' },
    { name: 'Photos', id: 'photos' },
];

const activeTab = ref(null);

// Mobile tab bar show/hide logic
const showTabBar = ref(true);
let lastScrollY = typeof window !== 'undefined' ? window.scrollY : 0;

const handleScroll = () => {
    const currentScrollY = window.scrollY;
    // If at (or near) top of page, clear activeTab
    if (currentScrollY <= 0) {
        activeTab.value = null;
    }
    // Mobile-specific show/hide tab bar logic
    if (window.innerWidth >= 768) {
        return;
    }
    if (currentScrollY < lastScrollY || currentScrollY < 50) {
        showTabBar.value = true;
    } else {
        showTabBar.value = false;
    }
    lastScrollY = currentScrollY;
};

const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

const handleResize = () => {
    if (typeof window !== 'undefined') {
        isMobile.value = window.innerWidth < 768;
    }
};

onMounted(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('scroll', handleScroll);
});

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
});

// Lead context detection (route-first logic)
// Treat as lead when:
// 1. Route is '/offers/new' (new internal lead draft) OR
// 2. offerRequest.is_lead is true (persisted lead record)
// This avoids dependence on user role load timing.
const isLead = computed(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path === '/offers/new') {
        return true;
    }
    return !!offerRequest.value?.is_lead;
});

const isSubmitted = computed(() => {
    return !!offerRequest.value?.is_submitted;
});

const superAdmin = computed(() => (isRole(profile.value).superAdmin()) || isRole(profile.value).admin());

const isAgentUser = computed(() => {
    return !!(profile.value && Array.isArray(profile.value.roles) && profile.value.roles.some(r => r.name === 'agent'));
});

const scrollToTab = (tab) => {
    activeTab.value = tab.id;
    const el = document.getElementById(tab.id);
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

const city = ref('');
const province = ref('');
const postalCode = ref('');
const propertyType = ref('');
const style = ref('');
const neighbourhood = ref('');
const property_age = ref(''); // now stores age value
const approxSqFt = ref('');
const locationConcerns = ref('');

const bedrooms = ref('');
const bathrooms = ref('');
const kitchens = ref('');
const basementDevelopment = ref('');
const heatingType = ref('');
const garageParking = ref('');
const floorCoverings = ref('');

const aluminumWiring = ref(''); 
const polyBPlumbing = ref('');
const overallCondition = ref('3'); // Default to 'Well Maintained'
// NOTE: original variable was misspelled (furnance vs furnace). Keep original for payload mapping, expose alias for template.
const furnanceAgeYears = ref('');
const furnaceAgeYears = furnanceAgeYears; // alias used in template
const hwtAgeYears = ref('');
const roofAgeYears = ref('');
const windowsAgeYears = ref('');
const notableConditions = ref('');

const minValue = ref('');
const maxValue = ref('');
const offersReceived = ref('');
const issueSelling = ref('');
const offerDeadline = ref('');
const saleTimeline = ref('');
const showingsDone = ref('');
const biggestChallenge = ref('');
const refusedRepairs = ref('');
const idealSaleDate = ref(null);
const sellerMotivation = ref('');
// Helper to create a blank MLS comparison row
const blankMlsComparisonRow = () => ({
    hash: null,
    number: '',
    address: '',
    sale_price: '',
    sale_date: null,
    sqft: '',
    year_built: '',
    _updating: false,
    _error: ''
});
// Prepopulate with 3 static blank rows
const mlsComparisons = ref([
    blankMlsComparisonRow(),
    blankMlsComparisonRow(),
    blankMlsComparisonRow()
]);

const t = (key, fallback) => {
    try {
        if (typeof window !== 'undefined' && window.__app__?.config?.globalProperties?.$t) {
            return window.__app__.config.globalProperties.$t(key);
        }
        if (typeof window !== 'undefined' && window.i18n?.global?.t) {
            return window.i18n.global.t(key);
        }
    } catch (e) { /* ignore */ }
    return fallback || key;
};

const mainAddress = ref('');
const agentsTodaysValue = ref('');
const houskiSaleValue = ref('');
const houskiListValue = ref('');
const solvAmount = ref('');
const valuationSource = ref('');

const agentFirstName = ref('');
const agentLastName = ref('');
const agentEmail = ref('');
const agentPhone = ref('');

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const intakeCreatedAt = ref(null);

const photos = ref([]);
const isPhotoDragOver = ref(false);
const previewPhoto = ref(null);

const is_call_requested = ref(false);
const is_evaluation_booked = ref(false);

const addPhotosFromFiles = (fileList) => {
    const files = Array.from(fileList || []);
    files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
            photos.value.push({
                id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                file,
                dataUrl: e.target.result,
                isNew: true
            });
        };
        reader.readAsDataURL(file);
    });
};

const handlePhotoInput = (e) => {
    if (e.target && e.target.files) {
        addPhotosFromFiles(e.target.files);
        e.target.value = '';
    }
};

const handlePhotoDragOver = (e) => {
    e.preventDefault();
    isPhotoDragOver.value = true;
};
const handlePhotoDragLeave = (e) => {
    e.preventDefault();
    isPhotoDragOver.value = false;
};
const handlePhotoDrop = (e) => {
    e.preventDefault();
    isPhotoDragOver.value = false;
    if (e.dataTransfer && e.dataTransfer.files.length) {
        addPhotosFromFiles(e.dataTransfer.files);
    }
};

const removePhoto = (id) => {
    photos.value = photos.value.filter(p => p.id !== id);
};

const openPhotoPreview = (photo) => {
    previewPhoto.value = photo;
};
const closePhotoPreview = () => {
    previewPhoto.value = null;
};

onMounted(() => {
    const escHandler = (e) => {
        if (e.key === 'Escape') {
            closePhotoPreview();
        }
    };
    window.addEventListener('keydown', escHandler);
    window.__offerEscHandler = escHandler;
});

onBeforeUnmount(() => {
    if (window.__offerEscHandler) {
        window.removeEventListener('keydown', window.__offerEscHandler);
        delete window.__offerEscHandler;
    }
});

const photoInput = ref(null);
const openPhotoPicker = () => {
    let el = photoInput.value;
    if (Array.isArray(el)) {
        el = el[0];
    }
    if (!el) {
        return;
    }
    if (typeof el.showPicker === 'function') {
        el.showPicker();
        return;
    }
    if (typeof el.click === 'function') {
        el.click();
    }
};

const buildOrderIntakePayload = () => {
    const booleanOrNull = (val) => {
        if (typeof val === 'boolean') {
            return val;
        }
        return val === 'yes' ? true : val === 'no' ? false : null;
    };
    let idealSaleDateFormatted = null;
    if (idealSaleDate.value) {
        if (idealSaleDate.value instanceof Date && !isNaN(idealSaleDate.value.getTime())) {
            idealSaleDateFormatted = format(idealSaleDate.value, 'yyyy-MM-dd');
        } else if (typeof idealSaleDate.value === 'string' && idealSaleDate.value.length <= 10) {
            idealSaleDateFormatted = idealSaleDate.value;
        } else {
            const parsed = new Date(idealSaleDate.value);
            if (!isNaN(parsed.getTime())) {
                idealSaleDateFormatted = format(parsed, 'yyyy-MM-dd');
            }
        }
    }
    const payload = {
        address: mainAddress.value || null,
        property_type: propertyType.value || null,
        postal_code: postalCode.value || null,
        style_of_house: style.value || null,
        neighbourhood: neighbourhood.value || null,
        property_age: property_age.value || null,
        sqft: approxSqFt.value || null,
        location_concerns: locationConcerns.value || null,
        city: city.value || null,
        province_id: province.value || null,
        bedrooms: bedrooms.value || null,
        bathrooms: (bathrooms.value || '').replace(/\./g, '_') || null, 
        kitchens_in_property: kitchens.value || null,
        basement: basementDevelopment.value || null,
        type_of_heating: heatingType.value || null,
        garage: garageParking.value || null,
        floor_coverings: floorCoverings.value || null,
        aluminum_wiring: aluminumWiring.value === '' ? null : aluminumWiring.value,
        poly_b_plumbing: polyBPlumbing.value === '' ? null : polyBPlumbing.value,
        overall_condition: overallCondition.value === '' ? null : overallCondition.value,
        furnance_age: furnanceAgeYears.value || null,
        hwt_age: hwtAgeYears.value || null,
        roof_age: roofAgeYears.value || null,
        windows_age: windowsAgeYears.value || null,
        notable_conditions: notableConditions.value || null,
        agent_first_name: agentFirstName.value || null,
        agent_last_name: agentLastName.value || null,
        agent_email: agentEmail.value || null,
        agent_phone: agentPhone.value || null,
        first_name: firstName.value || null,
        last_name: lastName.value || null,
        email: email.value || null,
        valuation: {
            solv_amount: solvAmount.value || null,
            min_value: minValue.value || null,
            max_value: maxValue.value || null,
            timeline: saleTimeline.value || null,
            showings_done: booleanOrNull(showingsDone.value),
            offers_received: offersReceived.value || null,
            issue_selling: issueSelling.value || null,
            biggest_challenge: biggestChallenge.value || null,
            deadline: booleanOrNull(offerDeadline.value),
            ideal_sale_date: idealSaleDateFormatted,
            seller_motivation: sellerMotivation.value || null,
            refused_repairs: booleanOrNull(refusedRepairs.value),
            agents_todays_value: agentsTodaysValue.value || null
        },
        images: photos.value.map(p => ({ image: p.dataUrl, original_filename: p.file?.name || null })),
        mls_comparisons: mlsComparisons.value
            .filter(m => (
                m.number
            ))
            .map(m => ({
                hash: m.hash || null,
                number: m.number,
            }))
    };
    return payload;
};

const isSavingOfferIntake = ref(false);
const isSubmittingOfferForm = ref(false);
const isNewOffer = computed(() => !offerRequest.value || !offerRequest.value.hash);
const offerIdentifier = computed(() => offerRequest.value?.hash || null);

// Agents cannot submit offer form if it is NOT a lead (i.e., already converted to an offer)
const canSubmitOfferForm = computed(() => {
    if (isAgentUser.value && !isLead.value) {
        return false;
    }
    return true;
});

const saveOfferIntakeHandler = async () => {
    if (isSavingOfferIntake.value) {
        return;
    }
    isSavingOfferIntake.value = true;
    errorStore.reset();
    try {
        const payload = buildOrderIntakePayload();
        if (isNewOffer.value) {
            await offersStore.saveOfferIntake(payload);
        } else if (offerIdentifier.value) {
            await offersStore.updateOfferIntake(offerIdentifier.value, payload);
        }
        toast.success(t('offer.offer_saved', isLead.value ? 'Lead saved successfully' : 'Offer saved successfully'));
        errorStore.reset();
    } catch (e) {
        const errs = e?.response?.data?.errors;
        if (e?.response?.status === 422 && errs) {
            errorStore.add(errs);
        }
        toast.error(t('offer.offer_save_failed', isLead.value ? 'Failed to save lead' : 'Failed to save offer'));
        console.error('Failed to save offer intake', e);
    } finally {
        isSavingOfferIntake.value = false;
    }
};

const submitOfferFormHandler = async () => {
    if (isSubmittingOfferForm.value) {
        return;
    }
    // Guard: agents cannot submit (button hidden but extra safety)
    if (!canSubmitOfferForm.value) {
        toast.error(t('offer.agents_cannot_submit_offer', 'Agents cannot submit this offer form.'));
        return;
    }
    isSubmittingOfferForm.value = true;
    errorStore.reset();
    try {
        const payload = buildOrderIntakePayload();
        if (offerIdentifier.value) {
            await offersStore.submitOfferForm(offerIdentifier.value, payload);
        } else {
            await offersStore.submitOfferForm(null, payload);
        }
        toast.success(t('offer.offer_submitted', 'Offer submitted successfully'));
        errorStore.reset();
    } catch (e) {
        const errs = e?.response?.data?.errors;
        if (e?.response?.status === 422 && errs) {
            errorStore.add(errs);
        }
        toast.error(t('offer.offer_submit_failed', 'Failed to submit offer'));
        console.error('Failed to submit offer form', e);
    } finally {
        isSubmittingOfferForm.value = false;
    }
};

const requestCashOfferHandler = async () => {
    if (!isAgentUser.value || !isLead.value) {
        toast.error(t('offer.only_agents_on_leads_can_request_cash_offer', 'Only agents can request a cash offer on leads.'));
        return;
    }
    if (isSubmittingOfferForm.value) {
        return;
    }
    isSubmittingOfferForm.value = true;
    errorStore.reset();
    try {
        const payload = buildOrderIntakePayload();
        await offersStore.requestCashOffer(offerIdentifier.value, payload);
        toast.success(t('offer.cash_offer_requested_success', 'Cash offer requested successfully'));
    } catch (e) {
        const errs = e?.response?.data?.errors;
        if (e?.response?.status === 422 && errs) {
            errorStore.add(errs);
        }
        toast.error(t('offer.cash_offer_request_failed', 'Failed to request cash offer'));
        console.error('Failed to request cash offer', e);
    } finally {
        isSubmittingOfferForm.value = false;
    }
};

const sellersOpinion = computed(() => {
    const min = parseFloat(minValue.value);
    const max = parseFloat(maxValue.value);
    if (!isNaN(min) && !isNaN(max)) {
        return ((min + max) / 2).toFixed(2);
    }
    return '';
});

const sellersOpinionModel = computed({
    get: () => sellersOpinion.value,
    set: () => {}
});

watch(offerRequest, (val) => {
    if (!val) {
        return;
    }    
    mainAddress.value = val.address || '';
    propertyType.value = val.property_type || '';
    postalCode.value = val.postal_code || '';
    city.value = val.city || '';
    province.value = val.province_id || '';
    style.value = val.style_of_house || '';
    neighbourhood.value = val.neighbourhood || '';
    property_age.value = val.property_age || '';
    approxSqFt.value = val.sqft || '';
    locationConcerns.value = val.location_concerns || '';
    bedrooms.value = val.bedrooms || '';
    bathrooms.value = (val.bathrooms || '').replace(/_/g, '.');
    kitchens.value = val.kitchens_in_property || '';
    basementDevelopment.value = val.basement || '';
    heatingType.value = val.type_of_heating || '';
    garageParking.value = val.garage || '';
    floorCoverings.value = val.floor_coverings || '';
    aluminumWiring.value = val.aluminum_wiring ?? '';
    polyBPlumbing.value = val.poly_b_plumbing ?? '';
    overallCondition.value = val.overall_condition ?? '3';
    furnanceAgeYears.value = val.furnance_age ?? '';
    hwtAgeYears.value = val.hwt_age ?? '';
    roofAgeYears.value = val.roof_age ?? '';
    windowsAgeYears.value = val.windows_age ?? '';
    notableConditions.value = val.notable_conditions ?? '';
    // Populate agent fields from offerRequest
    agentFirstName.value = val.agent_first_name || '';
    agentLastName.value = val.agent_last_name || '';
    agentEmail.value = val.agent_email || '';
    agentPhone.value = val.agent_phone || '';

    // If user is agent, populate agent fields from user store
    if (profile.value && Array.isArray(profile.value.roles)) {
        const isAgent = profile.value.roles.some(role => role.name === 'agent');
        if (isAgent) {
            agentFirstName.value = profile.value.first_name || agentFirstName.value;
            agentLastName.value = profile.value.last_name || agentLastName.value;
            agentEmail.value = profile.value.email || agentEmail.value;
        }
    }
    is_call_requested.value = val.is_call_requested || false;
    is_evaluation_booked.value = val.is_evaluation_booked || false;

    firstName.value = val.first_name || '';
    lastName.value = val.last_name || '';
    email.value = val.email || '';
    if (val.created_at) {
        let parsed = new Date(val.created_at);
        if (isNaN(parsed.getTime())) {
            parsed = new Date(val.created_at.replace(' ', 'T'));
        }
        intakeCreatedAt.value = isNaN(parsed.getTime()) ? null : parsed;
    } else {
        intakeCreatedAt.value = null;
    }

    const valuation = val.valuations?.[0];
    if (!valuation) {
        return;
    }

    minValue.value = valuation.min_value || '';
    maxValue.value = valuation.max_value || '';
    saleTimeline.value = valuation.timeline || '';
    showingsDone.value = valuation.showings_done === true ? 'yes' : (valuation.showings_done === false ? 'no' : '');
    offersReceived.value = valuation.offers_received || '';
    issueSelling.value = valuation.issue_selling || '';
    biggestChallenge.value = valuation.biggest_challenge || '';
    offerDeadline.value = valuation.deadline === true ? 'yes' : (valuation.deadline === false ? 'no' : '');
    idealSaleDate.value = valuation.ideal_sale_date || null;
    sellerMotivation.value = valuation.seller_motivation || '';
    refusedRepairs.value = valuation.refused_repairs === true ? 'yes' : (valuation.refused_repairs === false ? 'no' : '');
    agentsTodaysValue.value = valuation.agents_todays_value || '';
    solvAmount.value = valuation.solv_amount || '';
    houskiSaleValue.value = valuation.houski_sale_value ?? '';
    houskiListValue.value = valuation.houski_list_value ?? '';
    valuationSource.value = valuation.valuation_source ?? '';

    if (Array.isArray(valuation.mls_comparisons)) {
        mlsComparisons.value = valuation.mls_comparisons.map(m => ({
            hash: m.hash || null,
            number: m.number || '',
            _updating: false,
            _error: ''
        }));
    }
    // Ensure at least 3 static rows are always present
    while (mlsComparisons.value.length < 3) {
        mlsComparisons.value.push(blankMlsComparisonRow());
    }

    if (Array.isArray(val.documents)) {
        const hasUnsaved = photos.value.some(p => p.isNew);
        if (!hasUnsaved) {
            photos.value = val.documents.map(d => {
                const url = d.public_url;
                return {
                    id: d.hash || `${d.id}`,
                    document: d,
                    isNew: false,
                    dataUrl: url
                };
            }).filter(p => !!p.dataUrl);
        }
    }
}, { immediate: true });

const calculateCashOffer = () => {
    if (!superAdmin.value) {
        return;
    }

    offersStore.calculateCashOffer(offerIdentifier.value);
};

// Centralized lock rules replacing direct :disabled usage
const lockedFields = computed(() => ({
    'valuation.solv_amount': !superAdmin.value,
    'valuation.houski_sale_value': true,
    'valuation.houski_list_value': true,
    'valuation.valuation_source': true,
    'valuation.valuation_confidence': true,
    'valuation.min_value': isAgentUser.value,
    'valuation.max_value': isAgentUser.value,
}));

const isLocked = (field) => !!lockedFields.value[field];

const styleOptions = computed(() => {
    const selectedType = propertyTypeOptions.value.find(pt => pt.value === propertyType.value);
    if (!selectedType || !selectedType.allowed) {
        return [];
    }
    return styleAllOptions.value.filter(style => {
        return style.allowed && style.allowed.some(a => selectedType.allowed.includes(a));
    });
});

// Lead-specific UI label overrides
const pageTitle = computed(() => isLead.value ? 'Lead Form' : 'Cash Offer Form');
const pageDescription = computed(() => isLead.value ? 'Complete the form below to update lead information.' : 'Complete the form below to update offer information.');
const backButtonLabel = computed(() => isLead.value ? 'Back to Leads' : 'Back to Offers');
const saveButtonLabel = computed(() => isLead.value ? 'Save Lead' : 'Save Offer Intake');

</script>

<template>
<MainContentLayout>
    <div class="flex justify-between">
        <div>
            <h5 class="font-bold">{{ pageTitle }}</h5>
            <p class="text-grey-600">{{ pageDescription }}</p>
        </div>
        <div class="flex items-center gap-4">
            <div>
                <span v-if="is_evaluation_booked" class="font-semibold text-color">{{ $t('offer.evaluation_requested') }}</span>
                <span v-else-if="is_call_requested" class="font-semibold text-color">{{ $t('offer.call_requested') }}</span>
            </div>
            <StyledButton
                class=" custom-tab-btn"
                @click="backToOffers">
                {{ backButtonLabel }}
            </StyledButton>
        </div>
    </div>

    <!-- Mobile Tab Bar: fixed at top, horizontally scrollable, smoothly slides up/down -->
    <transition name="slide-top">
        <div v-if="showTabBar" class="fixed top-0 left-0 right-0 z-[1100] bg-white shadow-md flex justify-center md:hidden tab-bar-slide">
            <div class="pb-6 pr-2 pl-2 rounded-lg inline-flex w-full overflow-x-auto scrollbar-thin scrollbar-thumb-grey-400 scrollbar-track-grey-100 min-h-[64px]" style="-webkit-overflow-scrolling: touch;">
                <div class="absolute left-0 right-0 bottom-0 h-1 bg-grey-400 rounded-full" />
                <div class="flex space-x-8 items-center">
                    <button
                        v-for="tab in tabs"
                        :key="tab.id"
                        class="relative py-3 px-3 text-md font-bold text-grey-600 hover:text-green-900 transition-colors whitespace-nowrap rounded-lg"
                        type="button"
                        @click="scrollToTab(tab)"
                        @touchstart="(e) => e.target.classList.add('hovered')"
                        @touchend="(e) => e.target.classList.remove('hovered')">
                        {{ tab.name }}
                    </button>
                </div>
            </div>
        </div>
    </transition>

    <!-- Desktop Tab Bar: horizontally scrollable, not fixed -->
    <div v-if="!isMobile">
        <div class="relative mt-5 pb-1 flex justify-center">
            <div class="relative inline-flex">
                <div class="absolute left-0 right-0 bottom-0 h-1 bg-grey-400 rounded-full" />
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    class="relative v pb-3 mr-8 last:mr-0 text-base font-bold text-grey-600 hover:text-green-900 transition-colors"
                    type="button"
                    @click="scrollToTab(tab)">
                    {{ tab.name }}
                </button>
            </div>
        </div>
    </div>

    <!-- Valuation -->
    <div class="mt-4">
        <h6 class="font-bold mb-4">{{ $t('offer.valuation') }}</h6>
        <div class="ml-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <label class="font-bold flex flex-col">
                <span>{{ $t('offer.solv_cash_offer_amount') }}</span>
                <StyledInput
                    v-model="solvAmount"
                    name="valuation.solv_amount"
                    :readonly="isLocked('valuation.solv_amount')"
                    :class="['mt-1', isLocked('valuation.solv_amount') ? 'locked-field' : '']" />
            </label>
            <label class="font-bold flex flex-col">
                <span>{{ $t('offer.agents_todays_value') }}</span>
                <StyledInput
                    v-model="agentsTodaysValue"
                    name="valuation.agents_todays_value"
                    class="mt-1" />
            </label>
            <label class="font-bold flex flex-col">
                <span>{{ $t('offer.address') }}</span>
                <StyledInput
                    v-model="mainAddress"
                    class="mt-1" />
            </label>
            <label v-if="houskiSaleValue && superAdmin" class="font-bold flex flex-col">
                <span>{{ $t('offer.houski_sale_value') }}</span>
                <StyledInput v-model="houskiSaleValue" :readonly="isLocked('valuation.houski_sale_value')" :class="['mt-1', isLocked('valuation.houski_sale_value') ? 'locked-field' : '']" />
            </label>
            <label v-if="houskiListValue && superAdmin" class="font-bold flex flex-col">
                <span>{{ $t('offer.houski_list_value') }}</span>
                <StyledInput v-model="houskiListValue" :readonly="isLocked('valuation.houski_list_value')" :class="['mt-1', isLocked('valuation.houski_list_value') ? 'locked-field' : '']" />
            </label>
            <label v-if="valuationSource && superAdmin" class="font-bold flex flex-col">
                <span>{{ $t('offer.valuation_source') }}</span>
                <StyledInput v-model="valuationSource" :readonly="isLocked('valuation.valuation_source')" :class="['mt-1', isLocked('valuation.valuation_source') ? 'locked-field' : '']" />
            </label>
        </div>
        <StyledButton v-if="superAdmin" class="ml-8 custom-tab-btn" @click="calculateCashOffer">{{ $t('offer.get_valuation') }}</StyledButton>
    </div>

    <div class="mt-8 space-y-16">
        <section
            v-for="tab in tabs"
            :id="tab.id"
            :key="tab.id"
            class="scroll-mt-32">
            <h6 class="font-bold mb-4">{{ tab.name }}</h6>
            <div v-if="!isMobile && ((tab.id === activeTab && tab.id !== 'photos') || (tab.id === 'agents-contact-information' && activeTab === 'photos'))" class="mb-2">
                <div class="relative pb-1 flex justify-center">
                    <div class="relative inline-flex">
                        <div class="absolute left-0 right-0 bottom-0 h-1 bg-grey-400 rounded-full" />
                        <button
                            v-for="tabBtn in tabs"
                            :key="tabBtn.id"
                            class="relative pb-3 mr-8 last:mr-0 text-base font-bold text-grey-600 hover:text-green-900 transition-colors"
                            type="button"
                            @click="scrollToTab(tabBtn)">
                            {{ tabBtn.name }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="p-6 rounded-lg bg-white">
                <!-- Basic Information Section -->
                <div
                    v-if="tab.id === 'basic-information'"
                    class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('widget.preview.first_name') }}</span>
                        <StyledInput
                            v-model="firstName"
                            name="first_name"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('widget.preview.last_name') }}</span>
                        <StyledInput
                            v-model="lastName"
                            name="last_name"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col md:col-span-2">
                        <span>{{ $t('widget.preview.email') }}</span>
                        <StyledInput
                            v-model="email"
                            name="email"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col md:col-span-2">
                        <span>{{ $t('offer.intake_date') }}</span>
                        <StyledDatepicker
                            v-model="intakeCreatedAt"
                            format="yyyy-MM-dd"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.address') }}</span>
                        <StyledInput
                            v-model="mainAddress"
                            name="address"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('label.city') }}</span>
                        <StyledInput
                            v-model="city"
                            name="city"
                            :placeholder="$t('label.city')"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('label.province') }}</span>
                        <StyledDropdown
                            v-model="province"
                            name="province_id"
                            :options="provinceOptions"
                            :placeholder="$t('offer.select_province')"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('label.postal_code') }}</span>
                        <StyledMask
                            v-model="postalCode"
                            name="postal_code"
                            :options="{
                                mask: '@#@ #@#',
                                tokens: {
                                    '@': { pattern: /[A-Za-z]/, transform: v => v.toUpperCase() },
                                    '#': { pattern: /[0-9]/ }
                                }
                            }"
                            :placeholder="$t('label.postal_code')"
                            class="mt-1" />
                    </label>
                </div>

                <!-- Property Details Section -->
                <div
                    v-else-if="tab.id === 'property-details'"
                    class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.property_type') }}</span>
                        <StyledDropdown
                            v-model="propertyType"
                            name="property_type"
                            :options="propertyTypeOptions"
                            :placeholder="$t('label.style_of_house')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.style_of_house') }}</span>
                        <StyledDropdown
                            v-model="style"
                            name="style_of_house"
                            :options="styleOptions"
                            :placeholder="propertyType ? $t('label.select') : $t('offer.select_property_type_first')" />
                    </label>
                    <label class="font-bold flex flex-col md:col-span-2">
                        <span>{{ $t('offer.neighbourhood') }}</span>
                        <StyledInput
                            v-model="neighbourhood"
                            :placeholder="$t('offer.neighbourhood')"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.property_age') }}</span>
                        <StyledDropdown
                            v-model="property_age"
                            name="property_age"
                            :options="ageOptions"
                            :placeholder="$t('label.select_age')"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.approx_sq_ft') }}</span>
                        <StyledInput
                            v-model="approxSqFt"
                            name="sqft"
                            :placeholder="$t('offer.approx_sq_ft')"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col md:col-span-2">
                        <span>{{ $t('offer.location_concerns') }}</span>
                        <StyledInput
                            v-model="locationConcerns"
                            :placeholder="$t('offer.location_concerns')"
                            class="mt-1" />
                    </label>
                </div>
                <!-- Rooms & Features Section -->
                <div
                    v-else-if="tab.id === 'rooms-features'"
                    class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.bedrooms_total') }}</span>
                        <StyledDropdown
                            v-model="bedrooms"
                            name="bedrooms"
                            :options="bedroomsOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.bathrooms_total') }}</span>
                        <StyledDropdown
                            v-model="bathrooms"
                            name="bathrooms"
                            :options="bathroomsOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.kitchens_in_property') }}</span>
                        <StyledDropdown
                            v-model="kitchens"
                            name="kitchens_in_property"
                            :options="kitchensOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.basement_development') }}</span>
                        <StyledDropdown
                            v-model="basementDevelopment"
                            name="basement"
                            :options="basementDevelopmentOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col md:col-span-2">
                        <span>{{ $t('offer.type_of_heating') }}</span>
                        <StyledDropdown
                            v-model="heatingType"
                            name="heating_type"
                            :options="heatingTypeOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.garage_parking') }}</span>
                        <StyledDropdown
                            v-model="garageParking"
                            name="garage"
                            :options="garageParkingOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.floor_coverings') }}</span>
                        <StyledDropdown
                            v-model="floorCoverings"
                            name="floor_coverings"
                            :options="floorCoveringsOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                </div>
                <!-- Condition & Age Section -->
                <div
                    v-else-if="tab.id === 'condition-age'"
                    class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.aluminum_wiring_present') }}</span>
                        <StyledDropdown
                            v-model="aluminumWiring"
                            name="aluminum_wiring"
                            :options="yesNoOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.poly_b_plumbing_present') }}</span>
                        <StyledDropdown
                            v-model="polyBPlumbing"
                            name="poly_b_plumbing"
                            :options="yesNoOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.overall_condition') }}</span>
                        <StyledDropdown
                            v-model="overallCondition"
                            name="overall_condition"
                            :options="overallConditionOptions"
                            :placeholder="$t('label.select')" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.furnace_age_years') }}</span>
                        <StyledInput
                            v-model="furnaceAgeYears"
                            name="furnance_age"
                            type="number"
                            min="0"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.hwt_age_years') }}</span>
                        <StyledInput
                            v-model="hwtAgeYears"
                            type="number"
                            min="0"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.roof_age_years') }}</span>
                        <StyledInput
                            v-model="roofAgeYears"
                            type="number"
                            min="0"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.windows_age_years') }}</span>
                        <StyledInput
                            v-model="windowsAgeYears"
                            type="number"
                            min="0"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col md:col-span-2">
                        <span>{{ $t('offer.notable_conditions') }}</span>
                        <StyledTextArea
                            v-model="notableConditions"
                            rows="4"
                            class="mt-1" />
                    </label>
                </div>
                <!-- Sale Information & Valuation Section -->
                <div
                    v-else-if="tab.id === 'sale-information-valuation'"
                    class="space-y-8">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-1">
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.min_value') }}</span>
                            <StyledInput
                                v-model="minValue"
                                name="valuation.min_value"
                                type="number"
                                min="0"
                                :readonly="isLocked('valuation.min_value')"
                                :class="['mt-1', isLocked('valuation.min_value') ? 'locked-field' : '']" />
                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.max_value') }}</span>
                            <StyledInput
                                v-model="maxValue"
                                name="valuation.max_value"
                                type="number"
                                min="0"
                                :readonly="isLocked('valuation.max_value')"
                                :class="['mt-1', isLocked('valuation.max_value') ? 'locked-field' : '']" />
                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.sellers_opinion_value_mid') }}</span>
                            <StyledInput
                                v-model="sellersOpinionModel"
                                name="sellers_opinion_mid"
                                readonly
                                class="mt-1" />
                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.offers_received') }}</span>
                            <StyledInput v-model="offersReceived" name="offers_received" class="mt-1" />
                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.issue_selling') }}</span>
                            <StyledInput
                                v-model="issueSelling"
                                rows="3"
                                class="mt-1" />
                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.offer_deadline') }}</span>
                            <StyledDropdown
                                v-model="offerDeadline"
                                name="offer_deadline"
                                :options="yesNoOptions"
                                :placeholder="$t('label.select')" />
                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.timeline_of_selling') }}</span>
                            <StyledDropdown
                                v-model="saleTimeline"
                                name="sale_timeline"
                                :options="timelineOptions"
                                :placeholder="$t('label.select')" />                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.showings_done') }}</span>
                            <StyledDropdown
                                v-model="showingsDone"
                                :options="yesNoOptions"
                                class="mt-1" />
                        </label>        
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.biggest_challenge') }}</span>
                            <StyledInput
                                v-model="biggestChallenge"
                                rows="3"
                                class="mt-1" />
                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.refused_repairs') }}</span>
                            <StyledDropdown
                                v-model="refusedRepairs"
                                name="refused_repairs"
                                :options="yesNoOptions"
                                :placeholder="$t('label.select')" />
                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.ideal_sale_date') }}</span>
                            <StyledDatepicker v-model="idealSaleDate" name="ideal_sale_date" format="yyyy-MM-dd" />                        </label>
                        <label class="font-bold flex flex-col">
                            <span>{{ $t('offer.seller_motivation') }}</span>
                            <StyledDropdown
                                v-model="sellerMotivation"
                                name="seller_motivation"
                                :options="sellerMotivationOptions"
                                :placeholder="$t('label.select')" />                        </label>
                    </div>
                    <label class="font-bold flex flex-col">
                        <span>{{ $t('offer.agents_todays_value') }}</span>
                        <StyledInput 
                            v-model="agentsTodaysValue" 
                            class="mt-1"
                            name="valuation.agents_todays_value"
                            :placeholder="$t('label.enter_value')" />
                    </label>
                    <div>
                        <div class="flex flex-wrap items-center gap-3 mb-2">
                            <h6 class="font-bold mr-auto">{{ $t('offer.mls_comparisons') }}</h6>
                            <div v-if="errors && errors.mls_comparisons" class="text-red-600 text-md font-semibold mb-2">
                                {{ errors.mls_comparisons[0] }}
                            </div>
                        </div>
                        <div class="space-y-4">
                            <div
                                v-for="(row, idx) in mlsComparisons"
                                :key="idx"
                                class="p-4 rounded-lg bg-grey-600/10 border-2 border-grey-300">
                                <div class="flex justify-center items-center mb-2">
                                    <label class="block font-semibold text-center w-full">{{ $t('offer.mls_comparison') }} #{{ idx + 1 }}</label>
                                </div>
                                <div class="justify-center">
                                    <div class="flex flex-col items-center w-full">
                                        <span class="block font-semibold mb-1 text-center">{{ $t('offer.mls_number') }}</span>
                                        <StyledInput v-model="row.number" name="mls_comparisons.*.number" class="w-full md:w-2/3 text-center" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Agents Contact Information Section -->
                <div
                    v-else-if="tab.id === 'agents-contact-information'"
                    class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <label class="font-bold flex flex-col">
                        <span>{{ `Agent ${$t('widget.preview.first_name')}` }}</span>
                        <StyledInput
                            v-model="agentFirstName"
                            name="agent_first_name"
                            :placeholder="$t('widget.preview.first_name')"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ `Agent ${$t('widget.preview.last_name')}` }}</span>
                        <StyledInput
                            v-model="agentLastName"
                            name="agent_last_name"
                            :placeholder="$t('widget.preview.last_name')"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ `Agent ${$t('widget.preview.email')}` }}</span>
                        <StyledInput
                            v-model="agentEmail"
                            name="agent_email"
                            type="email"
                            :placeholder="$t('widget.preview.email')"
                            class="mt-1" />
                    </label>
                    <label class="font-bold flex flex-col">
                        <span>{{ `Agent ${$t('widget.preview.phone')}` }}</span>
                        <StyledMask
                            v-model="agentPhone"
                            name="agent_phone"
                            :options="{ mask: '###-###-####' }"
                            :placeholder="$t('widget.preview.phone')"
                            class="mt-1" />
                    </label>
                </div>
                <!-- Photos Section -->
                <div
                    v-else-if="tab.id === 'photos'"
                    class="space-y-6">
                    <div>
                        <div
                            class="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors"
                            :class="isPhotoDragOver ? 'border-blue-500 bg-blue-50' : 'border-grey-300 bg-grey-50'"
                            @dragover="handlePhotoDragOver"
                            @dragleave="handlePhotoDragLeave"
                            @drop="handlePhotoDrop"
                            @click="openPhotoPicker">
                            <p class="text-base text-grey-600 mb-2">{{ $t('offer.drag_drop_or_click_to_add_photos') }}</p>
                            <input
                                ref="photoInput"
                                data-photo-uploader="true"
                                type="file"
                                accept="image/*"
                                multiple
                                class="sr-only"
                                @change="handlePhotoInput">
                        </div>
                    </div>
                    <div v-if="photos.length" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        <div
                            v-for="p in photos"
                            :key="p.id"
                            class="relative group border-2 border-grey-300 rounded overflow-hidden bg-white cursor-pointer"
                            @click="openPhotoPreview(p)">
                            <img :src="p.dataUrl" :alt="$t('offer.photo') || 'Photo'" class="object-cover w-full h-32 transition-transform group-hover:scale-105">
                            <button
                                type="button"
                                class="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded opacity-90 hover:opacity-100"
                                @click.stop="removePhoto(p.id)">
                                {{ $t('label.remove') }}
                            </button>
                        </div>
                    </div>
                    <div v-if="!photos.length" class="text-sm text-grey-600">{{ $t('offer.no_photos_added_yet') || 'No photos added yet.' }}</div>
                    <div v-if="previewPhoto" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="closePhotoPreview">
                        <div class="relative max-w-3xl w-full">
                            <button
                                type="button"
                                class="absolute -top-10 right-0 btn focus:ring-2 hover:text-green-900 focus:ring-green-400 text-white p-1 rounded"
                                aria-label="Close"
                                @click="closePhotoPreview">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-6 w-6"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line
                                        x1="18"
                                        y1="6"
                                        x2="6"
                                        y2="18" />
                                    <line
                                        x1="6"
                                        y1="6"
                                        x2="18"
                                        y2="18" />
                                </svg>
                            </button>
                            <img :src="previewPhoto.dataUrl" class="w-full max-h-[80vh] object-contain rounded shadow-lg" :alt="$t('offer.photo') || 'Photo'">
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>

    <div class="flex flex-col md:flex-row justify-end space-y-2 md:space-y-0 md:space-x-2">
        <StyledButton
            v-if="!isSubmitted"
            type="button"
            :disabled="isSavingOfferIntake"
            class="custom-tab-btn"
            @click="saveOfferIntakeHandler">
            <span v-if="isSavingOfferIntake">{{ $t('label.saving') }}</span>
            <span v-else>{{ saveButtonLabel }}</span>
        </StyledButton>
        <StyledButton
            v-if="!isAgentUser && !isSubmitted"
            type="button"
            :disabled="isSubmittingOfferForm || !canSubmitOfferForm"
            class="custom-tab-btn"
            @click="submitOfferFormHandler">
            <span v-if="isSubmittingOfferForm">{{ $t('label.submitting') }}</span>
            <span v-else>{{ $t('offer.submit_offer_form') || 'Submit' }}</span>
        </StyledButton>
        <StyledButton
            v-if="isAgentUser && isLead"
            type="button"
            :disabled="isSubmittingOfferForm"
            class="custom-tab-btn"
            @click="requestCashOfferHandler">
            <span v-if="isSubmittingOfferForm">{{ $t('label.submitting') }}</span>
            <span v-else>{{ $t('offer.request_cash_offer') || 'Request Cash Offer' }}</span>
        </StyledButton>
    </div>
</MainContentLayout>
</template>

<style scoped>
    .custom-tab-btn {
        background: #00594C !important;
    }
</style>
